import { domainColors } from './domains';
import {
  DEVICE_ADMIN,
  DEVICE_ROLES,
  GATEWAY_ADMIN,
  GATEWAY_ROLES,
  PLATFORM_ADMIN_ROLES,
  PLATFORM_ROLES,
  Role,
  SITE_ROLES,
  USER_ROLES,
} from './role.model';

/**
 * Navigation de wheere-inside — maquette `docs/maquettes/journal-audit/prototype.html`, validée
 * par Eddy le 2026-10-07 : Sites · Équipements · Organisations · Administration ▾. Accueil est sorti
 * de la barre (décision du 2026-10-07) : le logo y ramène (`/` → `/home`), la route reste.
 *
 * Aucune page n'a changé d'URL (sauf le journal, `/users/audit` → `/admin/journal`) : les entrées
 * regroupent des pages existantes par préfixes (`match`, préfixe le plus long gagnant — voir
 * `findActiveNav` de shared-shell). Customer n'est plus dans la barre : menu du compte › Application.
 */
export interface InsideNavLeaf {
  id: string;
  /** Clé Transloco du libellé. */
  labelKey: string;
  /** Clé Transloco de la ligne d'explication (entrées de menu déroulant). */
  descriptionKey?: string;
  link: string;
  /** Préfixes d'URL qui allument l'entrée (défaut : `[link]`). */
  match?: string[];
  /** Rôles exigés par la route (`app.routes.ts`, routes de section). */
  roles: readonly Role[];
}

export interface InsideNavEntry extends InsideNavLeaf {
  color: string;
  /** Présent → menu déroulant (barre) / section (tiroir mobile). */
  children?: InsideNavLeaf[];
}

/** Pages d'Administration › Outils, qui gardent leur URL d'origine. */
const TOOLS_PREFIXES = [
  '/tools',
  '/devices/supervised',
  '/devices/logging-sessions',
  '/devices/firmwares',
  '/devices/nn-builder',
  '/gateways/firmwares',
  '/gateways/default-parameters',
];

export const INSIDE_NAV: readonly InsideNavEntry[] = [
  { id: 'sites', labelKey: 'nav.sites', link: '/sites', color: domainColors.sites, roles: SITE_ROLES },
  {
    id: 'equipment',
    labelKey: 'nav.equipment',
    link: '/devices',
    match: ['/devices', '/gateways'],
    color: domainColors.devices,
    roles: [...new Set([...DEVICE_ROLES, ...GATEWAY_ROLES])],
  },
  {
    id: 'organizations',
    labelKey: 'nav.organizations',
    link: '/users',
    match: ['/users'],
    color: domainColors.users,
    roles: USER_ROLES,
  },
  {
    id: 'admin',
    labelKey: 'nav.administration',
    link: '/admin/journal',
    color: domainColors.tools,
    roles: PLATFORM_ROLES,
    children: [
      {
        id: 'journal',
        labelKey: 'nav.auditLog',
        descriptionKey: 'nav.auditLogDescription',
        link: '/admin/journal',
        match: ['/admin', '/users/audit'],
        roles: PLATFORM_ROLES,
      },
      {
        // Ex-« Manage ». Mêmes rôles que la garde de sa route (`app.routes.ts`).
        id: 'attributions',
        labelKey: 'nav.attributions',
        descriptionKey: 'nav.attributionsDescription',
        link: '/manage',
        roles: PLATFORM_ADMIN_ROLES,
      },
      {
        id: 'tools',
        labelKey: 'nav.tools',
        descriptionKey: 'nav.toolsDescription',
        link: '/tools',
        match: TOOLS_PREFIXES,
        roles: SITE_ROLES,
      },
      {
        // Ex-boutons « 🧪 Create fake … » des listes (2026-10-07). PLATFORM_ADMIN seul : les
        // endpoints `POST /trackers/fake` et `/gateways/fake` exigent `platform:fake`.
        id: 'test-equipment',
        labelKey: 'nav.testEquipment',
        descriptionKey: 'nav.testEquipmentDescription',
        link: '/admin/equipements-test',
        roles: PLATFORM_ADMIN_ROLES,
      },
    ],
  },
];

/** Même règle que `AuthStore.hasAnyRole` (PLATFORM_ADMIN passe partout), en fonction pure. */
function allowed(userRoles: readonly string[], required: readonly Role[]): boolean {
  if (!required.length) return true;
  return userRoles.includes('PLATFORM_ADMIN') || required.some(r => userRoles.includes(r));
}

/** Barre filtrée par rôle ; un menu déroulant dont aucune entrée n'est permise disparaît. */
export function insideNavFor(userRoles: readonly string[]): InsideNavEntry[] {
  return INSIDE_NAV.filter(e => allowed(userRoles, e.roles))
    .map(e => (e.children ? { ...e, children: e.children.filter(c => allowed(userRoles, c.roles)) } : e))
    .filter(e => !e.children || e.children.length > 0);
}

// ── Onglets de section ──────────────────────────────────────────────────────────────────────

export interface InsideSectionTab {
  label: string;
  link: string;
  roles: readonly Role[];
}

const pathOf = (url: string) => url.split(/[?#]/)[0];
const forRoles = (tabs: InsideSectionTab[], userRoles: readonly string[]) =>
  tabs.filter(t => allowed(userRoles, t.roles));

/** Équipements = Trackers + Gateways. */
export function equipmentTabs(userRoles: readonly string[]): InsideSectionTab[] {
  return forRoles(
    [
      { label: 'Trackers', link: '/devices/trackers', roles: DEVICE_ROLES },
      { label: 'Gateways', link: '/gateways/list', roles: GATEWAY_ROLES },
    ],
    userRoles
  );
}

/** Organisations = Organisations + Utilisateurs (+ Sans organisation, conservé). Le journal est passé dans Administration. */
export function organizationTabs(userRoles: readonly string[]): InsideSectionTab[] {
  return forRoles(
    [
      { label: 'Organisations', link: '/users/organizations', roles: USER_ROLES },
      { label: 'Utilisateurs', link: '/users/all', roles: PLATFORM_ROLES },
      { label: 'Sans organisation', link: '/users/orphans', roles: PLATFORM_ROLES },
    ],
    userRoles
  );
}

/** Firmwares réunis : un onglet, deux cibles. */
export function firmwareTargetTabs(): InsideSectionTab[] {
  return [
    { label: 'Trackers', link: '/devices/firmwares', roles: DEVICE_ADMIN },
    { label: 'Gateways', link: '/gateways/firmwares', roles: GATEWAY_ADMIN },
  ];
}

/** Supervisés réunit les sessions supervisées et les sessions de logs. */
export function supervisedTabs(): InsideSectionTab[] {
  return [
    { label: 'Sessions supervisées', link: '/devices/supervised', roles: DEVICE_ROLES },
    { label: 'Sessions de logs', link: '/devices/logging-sessions', roles: DEVICE_ROLES },
  ];
}

/**
 * Administration › Outils. Un onglet qui réunit deux pages (Firmwares, Supervisés) pointe la page
 * affichée quand on y est, pour rester allumé ; sinon sa première page.
 */
export function toolsTabs(userRoles: readonly string[], url: string): InsideSectionTab[] {
  const path = pathOf(url);
  const stay = (tabs: InsideSectionTab[]) =>
    (tabs.find(t => path === t.link || path.startsWith(t.link + '/')) ?? tabs[0]).link;
  return forRoles(
    [
      { label: 'Outils de terrain', link: '/tools', roles: SITE_ROLES },
      { label: 'Firmwares', link: stay(firmwareTargetTabs()), roles: DEVICE_ADMIN },
      { label: 'NN Builder', link: '/devices/nn-builder', roles: DEVICE_ADMIN },
      { label: 'Supervisés', link: stay(supervisedTabs()), roles: DEVICE_ROLES },
      { label: 'Paramètres par défaut', link: '/gateways/default-parameters', roles: GATEWAY_ROLES },
    ],
    userRoles
  );
}
